import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axios";
import { Loading } from "../../Other/Loading";
import toast from "react-hot-toast";
import { Trash2, Plus, Sparkles, Check, Tag, Info, Percent, Edit2, X, ArrowLeft } from "lucide-react";
import "./CreateCouponPage.scss";

const CreateCouponPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // View state toggle
  const [showForm, setShowForm] = useState(false);

  // Form State
  const [code, setCode] = useState("");
  const [minCourses, setMinCourses] = useState("2");
  const [discountPercent, setDiscountPercent] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);

  // Edit State
  const [editingCouponId, setEditingCouponId] = useState(null);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const [couponsRes, coursesRes] = await Promise.all([
        axiosInstance.get("/coupons"),
        axiosInstance.get("/course")
      ]);
      if (couponsRes?.data && !couponsRes.data.error) {
        setCoupons(couponsRes.data.data);
      }
      if (coursesRes?.data) {
        const rawCourses = coursesRes.data.data?.data || coursesRes.data.data || [];
        const publishedCourses = rawCourses.filter(c => c.Status === 3);
        setCourses(publishedCourses);
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      toast.error("Failed to load coupon configurations.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (coupon) => {
    setEditingCouponId(coupon.id);
    setCode(coupon.code);
    setMinCourses(String(coupon.minCourses || "1"));
    setDiscountPercent(String(coupon.discountPercent));
    setSelectedCourseId(coupon.courseId ? String(coupon.courseId) : "");
    setMinAmount(coupon.minAmount ? String(coupon.minAmount) : "");
    
    // Find the targeted course name to populate the search query
    const targetCourse = courses.find((c) => Number(c.ID || c.id) === Number(coupon.courseId));
    setCourseSearch(targetCourse ? targetCourse.CourseName : "");
    
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditingCouponId(null);
    setCode("");
    setMinCourses("2");
    setDiscountPercent("");
    setSelectedCourseId("");
    setMinAmount("");
    setCourseSearch("");
    setShowCourseDropdown(false);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code.trim()) {
      toast.error("Please enter a Coupon Code");
      return;
    }

    const discountVal = parseFloat(discountPercent);
    if (isNaN(discountVal) || discountVal <= 0 || discountVal > 100) {
      toast.error("Please enter a valid Discount Percentage between 1% and 100%");
      return;
    }

    const minCoursesVal = parseInt(minCourses);
    if (isNaN(minCoursesVal) || minCoursesVal < 1) {
      toast.error("Please enter a valid course requirement");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        code: code.trim().toUpperCase(),
        minCourses: minCoursesVal,
        discountPercent: discountVal,
        courseId: selectedCourseId ? parseInt(selectedCourseId) : null,
        minAmount: minAmount ? parseFloat(minAmount) : 0.00
      };

      if (editingCouponId) {
        const res = await axiosInstance.put(`/coupons/${editingCouponId}`, payload);
        if (res?.data && !res.data.error) {
          toast.success("Coupon updated successfully!");
          fetchCoupons();
          handleCancel();
        } else {
          toast.error(res?.data?.message || "Failed to update coupon.");
        }
      } else {
        const res = await axiosInstance.post("/coupons", payload);
        if (res?.data && !res.data.error) {
          toast.success("Coupon created successfully!");
          fetchCoupons();
          handleCancel();
        } else {
          toast.error(res?.data?.message || "Failed to create coupon.");
        }
      }
    } catch (error) {
      console.error("Error saving coupon:", error);
      toast.error(error.response?.data?.message || "Internal server error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon? This action cannot be undone.")) {
      return;
    }

    try {
      const res = await axiosInstance.delete(`/coupons/${id}`);
      if (res?.data && !res.data.error) {
        toast.success("Coupon deleted successfully!");
        fetchCoupons();
      } else {
        toast.error(res?.data?.message || "Failed to delete coupon.");
      }
    } catch (error) {
      console.error("Error deleting coupon:", error);
      toast.error("Internal server error occurred while deleting coupon.");
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="create-coupon-container animate__animated animate__fadeIn">
      {/* Header section */}
      <div className="coupon-header">
        <div className="header-left">
          <h1 className="header-title">
            <Sparkles className="icon-sparkle" size={24} />
            Manage Cart Coupons
          </h1>
          <p className="header-subtitle">
            Configure dynamic percentage discounts based on the number of courses/languages in the student's cart.
          </p>
        </div>
        {!showForm && (
          <button className="btn-add-coupon" onClick={() => setShowForm(true)}>
            <Plus size={18} />
            <span>Create Coupon</span>
          </button>
        )}
      </div>

      {/* Main Content Layout */}
      {showForm ? (
        <div className="coupon-form-card">
          <div className="form-card-header">
            <button className="btn-back" onClick={handleCancel}>
              <ArrowLeft size={18} />
              <span>Back to Listings</span>
            </button>
            <h2 className="form-title">
              {editingCouponId ? "Edit Coupon Settings" : "Configure New Coupon"}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="coupon-form">
            <div className="form-grid">
              {/* Left Column: Form Fields */}
              <div className="form-inputs-pane">
                <div className="form-group">
                  <label htmlFor="coupon-code">Coupon Code</label>
                  <div className="input-wrapper">
                    <Tag className="input-icon" size={18} />
                    <input
                      id="coupon-code"
                      type="text"
                      placeholder="e.g. MULTI10, SAVE20"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      disabled={submitting}
                      className="form-input text-uppercase"
                    />
                  </div>
                  <small className="help-text">
                    Unique identifier students enter in checkout (auto-converted to UPPERCASE).
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group half-width" style={{ position: "relative" }}>
                    <label htmlFor="target-course">Target Course (Optional)</label>
                    <div className="input-wrapper">
                      <Info className="input-icon" size={18} />
                      <input
                        id="target-course"
                        type="text"
                        placeholder="Search & select a course..."
                        value={courseSearch}
                        onChange={(e) => {
                          setCourseSearch(e.target.value);
                          if (!e.target.value) {
                            setSelectedCourseId("");
                          }
                        }}
                        onFocus={() => setShowCourseDropdown(true)}
                        onBlur={() => setShowCourseDropdown(false)}
                        disabled={submitting}
                        className="form-input"
                        autoComplete="off"
                      />
                      {courseSearch && (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setCourseSearch("");
                            setSelectedCourseId("");
                          }}
                          style={{
                            position: "absolute",
                            right: "10px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#94a3b8",
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {showCourseDropdown && (
                      <div className="searchable-dropdown-list">
                        <div
                          className="dropdown-item-option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setCourseSearch("");
                            setSelectedCourseId("");
                            setShowCourseDropdown(false);
                          }}
                        >
                          <em>All Courses (No Specific Course)</em>
                        </div>
                        {courses
                          .filter((c) =>
                            c.CourseName.toLowerCase().includes(courseSearch.toLowerCase())
                          )
                          .map((course) => (
                            <div
                              key={course.ID || course.id}
                              className="dropdown-item-option"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setCourseSearch(course.CourseName);
                                setSelectedCourseId(String(course.ID || course.id));
                                setShowCourseDropdown(false);
                              }}
                            >
                              {course.CourseName}
                            </div>
                          ))}
                        {courses.filter((c) =>
                          c.CourseName.toLowerCase().includes(courseSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="dropdown-item-empty">No matching courses found</div>
                        )}
                      </div>
                    )}
                    <small className="help-text">Type to search and select a specific course.</small>
                  </div>

                  <div className="form-group half-width">
                    <label htmlFor="min-amount">Minimum Course Price ($) (Optional)</label>
                    <div className="input-wrapper">
                      <Percent className="input-icon" size={18} />
                      <input
                        id="min-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="e.g. 100"
                        value={minAmount}
                        onChange={(e) => setMinAmount(e.target.value)}
                        disabled={submitting}
                        className="form-input"
                      />
                    </div>
                    <small className="help-text">Minimum price of the course to qualify.</small>
                  </div>
                </div>

                <div className="form-row">
                  {!selectedCourseId && (
                    <div className="form-group half-width">
                      <label htmlFor="min-courses">Courses / Languages Required</label>
                      <div className="input-wrapper">
                        <Info className="input-icon" size={18} />
                        <select
                          id="min-courses"
                          value={minCourses}
                          onChange={(e) => setMinCourses(e.target.value)}
                          disabled={submitting}
                          className="form-input"
                        >
                          <option value="1">1 Course</option>
                          <option value="2">2 Courses</option>
                          <option value="3">3 Courses</option>
                          <option value="4">4 Courses</option>
                          <option value="5">5+ Courses</option>
                        </select>
                      </div>
                      <small className="help-text">Minimum quantity to qualify.</small>
                    </div>
                  )}

                  <div className="form-group half-width">
                    <label htmlFor="discount-percent">Discount Percentage (%)</label>
                    <div className="input-wrapper">
                      <Percent className="input-icon" size={18} />
                      <input
                        id="discount-percent"
                        type="number"
                        min="1"
                        max="100"
                        placeholder="e.g. 10 or 20"
                        value={discountPercent}
                        onChange={(e) => setDiscountPercent(e.target.value)}
                        disabled={submitting}
                        className="form-input"
                      />
                    </div>
                    <small className="help-text">Discount percent applied to total.</small>
                  </div>
                </div>
              </div>

              {/* Right Column: Visual Summary Preview */}
              <div className="preview-pane">
                <h3 className="preview-pane-title">Live Settings Summary</h3>
                <div className="coupon-preview-card">
                  <div className="preview-card-inner">
                    <div className="preview-badge">ACTIVE RULE</div>
                    <h4 className="preview-code">{code.trim().toUpperCase() || "NEW_CODE"}</h4>
                    <div className="preview-details">
                      <div className="preview-stat">
                        <span className="stat-value">{discountPercent || "0"}%</span>
                        <span className="stat-label">DISCOUNT</span>
                      </div>
                      {!selectedCourseId ? (
                        <div className="preview-stat">
                          <span className="stat-value">{minCourses}</span>
                          <span className="stat-label">COURSES REQ.</span>
                        </div>
                      ) : (
                        <div className="preview-stat">
                          <span className="stat-value">SPECIFIC</span>
                          <span className="stat-label">COURSE ONLY</span>
                        </div>
                      )}
                    </div>
                    <p className="preview-explanation">
                      {selectedCourseId ? (
                        <>
                          Students purchasing the course{" "}
                          <strong>
                            {courses.find(c => String(c.ID || c.id) === String(selectedCourseId))?.CourseName || "selected course"}
                          </strong>
                          {minAmount && Number(minAmount) > 0 ? (
                            <> with a price of at least <strong>${Number(minAmount).toFixed(2)}</strong></>
                          ) : (
                            ""
                          )}{" "}
                          will receive a <strong>{discountPercent || "0"}%</strong> discount on that course.
                        </>
                      ) : (
                        <>
                          Students purchasing at least <strong>{minCourses}</strong> course(s)
                          {minAmount && Number(minAmount) > 0 ? (
                            <> (each costing at least <strong>${Number(minAmount).toFixed(2)}</strong>)</>
                          ) : (
                            ""
                          )}{" "}
                          will be eligible to get a <strong>{discountPercent || "0"}%</strong> discount off their subtotal.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={handleCancel} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? (
                  "Saving Settings..."
                ) : (
                  <>
                    <Check size={18} />
                    <span>{editingCouponId ? "Update Coupon" : "Activate Coupon"}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="coupon-listings-grid">
          {coupons.length === 0 ? (
            <div className="empty-listings-card">
              <Tag size={48} className="empty-icon" />
              <h3 className="empty-title">No Coupons Configured Yet</h3>
              <p className="empty-description">
                Create dynamic cart rules to reward students who purchase multiple courses.
              </p>
              <button className="btn-add-coupon-inline" onClick={() => setShowForm(true)}>
                Create First Coupon
              </button>
            </div>
          ) : (
            <div className="listings-grid">
              {coupons.map((coupon) => (
                <div className="coupon-listing-card" key={coupon.id}>
                  <div className="card-top">
                    <div className="coupon-pill">{coupon.code}</div>
                    <span className="discount-badge">{Number(coupon.discountPercent)}% OFF</span>
                  </div>
                  <div className="card-middle">
                    <div className="requirement-row">
                      <span className="label">Requires:</span>
                      <span className="value">
                        {coupon.courseId ? (
                          <>
                            Course:{" "}
                            <strong>
                              {courses.find(c => Number(c.ID || c.id) === Number(coupon.courseId))?.CourseName || `Course #${coupon.courseId}`}
                            </strong>
                            {Number(coupon.minAmount) > 0 && ` (Min Price: $${Number(coupon.minAmount)})`}
                          </>
                        ) : (
                          <>
                            {coupon.minCourses} Course{coupon.minCourses > 1 ? "s" : ""}
                            {Number(coupon.minAmount) > 0 && ` (Min Course Price: $${Number(coupon.minAmount)})`}
                          </>
                        )}
                      </span>
                    </div>
                    <div className="created-row">
                      <span className="label">Created:</span>
                      <span className="value">
                        {new Date(coupon.createdAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="card-divider"></div>
                  <div className="card-actions">
                    <button className="btn-edit" onClick={() => handleEditClick(coupon)}>
                      <Edit2 size={15} />
                      <span>Edit</span>
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(coupon.id)}>
                      <Trash2 size={15} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreateCouponPage;
